import { DEFAULT_LOCALE, SupportedLocale } from "./locales";

export type NavigationCopy = {
  links: {
    tools: string;
    useCases: string;
    pricing: string;
    features: string;
    resources: string;
    podcast: { title: string; description: string };
    videoToText: { title: string; description: string };
    instagramToText: { title: string; description: string };
    tiktokToTranscript: { title: string; description: string };
    instagramAnalytics: { title: string; description: string };
    reelToPost: { title: string; description: string };
    itineraryExtractor: { title: string; description: string };
    cooking: { title: string; description: string };
    videoBlog: { title: string; description: string };
    support: { title: string; description: string };
    feedback: { title: string; description: string };
    blog: { title: string; description: string };
  };
  actions: {
    login: string;
    signup: string;
    dashboard: string;
  };
};

const navigationCopy: Record<SupportedLocale, NavigationCopy> = {
  en: {
    links: {
      tools: "Tools",
      useCases: "Use Cases",
      pricing: "Pricing",
      features: "Features",
      resources: "Resources",
      podcast: {
        title: "Podcast Transcription",
        description: "Transcribe podcasts into clean searchable text.",
      },
      videoToText: {
        title: "Video to Text",
        description: "Get automatic text from videos.",
      },
      instagramToText: {
        title: "Instagram to Transcript",
        description: "Turn Instagram videos into clean transcripts fast.",
      },
      tiktokToTranscript: {
        title: "TikTok to Transcript",
        description: "Turn TikTok videos into clean transcripts fast.",
      },
      instagramAnalytics: {
        title: "Instagram Reel Analytics",
        description: "Score hooks, analyze comments, and get AI creative briefs.",
      },
      reelToPost: {
        title: "Reel to LinkedIn + Newsletter",
        description: "Repurpose creator Reels into LinkedIn and email drafts.",
      },
      itineraryExtractor: {
        title: "Travel Reel Location Extractor",
        description: "Extract every location mentioned in a travel Reel.",
      },
      cooking: {
        title: "Cooking Video to Recipe converter",
        description: "Get recipe from a video automatically.",
      },
      videoBlog: {
        title: "Video to Blog Converter",
        description: "Create a blog post from a video.",
      },
      support: {
        title: "Support",
        description: "Contact us — we're happy to help.",
      },
      feedback: {
        title: "Suggestions",
        description: "Leave suggestions to help us improve.",
      },
      blog: {
        title: "Blog",
        description: "Read our blog posts.",
      },
    },
    actions: {
      login: "Login",
      signup: "Sign Up",
      dashboard: "My Dashboard",
    },
  },
  es: {
    links: {
      tools: "Herramientas",
      useCases: "Casos de uso",
      pricing: "Precios",
      features: "Funciones",
      resources: "Recursos",
      podcast: {
        title: "Transcripción de podcast",
        description: "Transcribe podcasts a texto limpio y buscable.",
      },
      videoToText: {
        title: "Video a texto",
        description: "Obtén texto automático desde videos.",
      },
      instagramToText: {
        title: "Instagram a transcripción",
        description: "Convierte videos de Instagram en transcripciones rápidas.",
      },
      tiktokToTranscript: {
        title: "TikTok a transcripción",
        description: "Convierte videos de TikTok en transcripciones rápidas.",
      },
      instagramAnalytics: {
        title: "Analytics de Reels de Instagram",
        description: "Puntúa hooks, analiza comentarios y obtén briefs creativos con IA.",
      },
      reelToPost: {
        title: "Reel a LinkedIn + Newsletter",
        description: "Convierte Reels en borradores para LinkedIn y email.",
      },
      itineraryExtractor: {
        title: "Extractor de ubicaciones de Reels",
        description: "Extrae todas las ubicaciones mencionadas en un Reel de viajes.",
      },
      cooking: {
        title: "Video de cocina a receta",
        description: "Convierte videos de cocina en recetas.",
      },
      videoBlog: {
        title: "Video a blog",
        description: "Crea un blog a partir de un video.",
      },
      support: {
        title: "Soporte",
        description: "Contáctanos, estamos aquí para ayudar.",
      },
      feedback: {
        title: "Comentarios",
        description: "Deja comentarios para mejorar.",
      },
      blog: {
        title: "Blog",
        description: "Lee nuestros artículos.",
      },
    },
    actions: {
      login: "Iniciar sesión",
      signup: "Registrarse",
      dashboard: "Mi panel",
    },
  },
  fr: {
    links: {
      tools: "Outils",
      useCases: "Cas d'usage",
      pricing: "Tarifs",
      features: "Fonctionnalités",
      resources: "Ressources",
      podcast: {
        title: "Transcription de podcast",
        description: "Transcrivez les podcasts en texte propre et consultable.",
      },
      videoToText: {
        title: "Vidéo en texte",
        description: "Obtenez automatiquement du texte à partir d’une vidéo.",
      },
      instagramToText: {
        title: "Instagram en transcription",
        description: "Transformez des vidéos Instagram en transcriptions rapidement.",
      },
      tiktokToTranscript: {
        title: "TikTok en transcription",
        description: "Transformez des vidéos TikTok en transcriptions rapidement.",
      },
      instagramAnalytics: {
        title: "Analytics Reels Instagram",
        description: "Scorez les hooks, analysez les commentaires et obtenez des briefs IA.",
      },
      reelToPost: {
        title: "Reel vers LinkedIn + Newsletter",
        description: "Transformez des Reels en brouillons LinkedIn et email.",
      },
      itineraryExtractor: {
        title: "Extracteur de lieux de voyage",
        description: "Extrayez tous les lieux cités dans un Reel de voyage.",
      },
      cooking: {
        title: "Vidéo de cuisine en recette",
        description: "Transformez une vidéo de cuisine en recette.",
      },
      videoBlog: {
        title: "Vidéo vers blog",
        description: "Créez un article de blog à partir d'une vidéo.",
      },
      support: {
        title: "Support",
        description: "Contactez-nous, nous sommes là pour aider.",
      },
      feedback: {
        title: "Avis",
        description: "Laissez des retours pour nous améliorer.",
      },
      blog: {
        title: "Blog",
        description: "Lisez nos articles.",
      },
    },
    actions: {
      login: "Connexion",
      signup: "S'inscrire",
      dashboard: "Mon tableau de bord",
    },
  },
  de: {
    links: {
      tools: "Werkzeuge",
      useCases: "Anwendungsfälle",
      pricing: "Preise",
      features: "Funktionen",
      resources: "Ressourcen",
      podcast: {
        title: "Podcast-Transkription",
        description: "Transkribiere Podcasts in sauberen, durchsuchbaren Text.",
      },
      videoToText: {
        title: "Video zu Text",
        description: "Erhalte automatisch Text aus Videos.",
      },
      instagramToText: {
        title: "Instagram zu Transkript",
        description: "Wandle Instagram-Videos schnell in Transkripte um.",
      },
      tiktokToTranscript: {
        title: "TikTok zu Transkript",
        description: "Wandle TikTok-Videos schnell in Transkripte um.",
      },
      instagramAnalytics: {
        title: "Instagram Reel Analytics",
        description: "Hooks bewerten, Kommentare analysieren, KI-Briefs erhalten.",
      },
      reelToPost: {
        title: "Reel zu LinkedIn + Newsletter",
        description: "Wandle Reels in LinkedIn- und E-Mail-Entwürfe um.",
      },
      itineraryExtractor: {
        title: "Reise-Reel Ortsliste",
        description: "Extrahiere alle Orte aus einem Reise-Reel.",
      },
      cooking: {
        title: "Kochvideo zu Rezept",
        description: "Erstelle Rezepte automatisch aus Kochvideos.",
      },
      videoBlog: {
        title: "Video zu Blog",
        description: "Erstelle einen Blogbeitrag aus einem Video.",
      },
      support: {
        title: "Support",
        description: "Kontaktiere uns, wir helfen gern.",
      },
      feedback: {
        title: "Vorschläge",
        description: "Hinterlasse Vorschläge zur Verbesserung.",
      },
      blog: {
        title: "Blog",
        description: "Lies unsere Blogartikel.",
      },
    },
    actions: {
      login: "Anmelden",
      signup: "Registrieren",
      dashboard: "Mein Dashboard",
    },
  },
  it: {
    links: {
      tools: "Strumenti",
      useCases: "Casi d'uso",
      pricing: "Prezzi",
      features: "Funzionalità",
      resources: "Risorse",
      podcast: {
        title: "Trascrizione podcast",
        description: "Trascrivi podcast in testo pulito e consultabile.",
      },
      videoToText: {
        title: "Video in testo",
        description: "Ottieni testo automatico dai video.",
      },
      instagramToText: {
        title: "Instagram in trascrizione",
        description: "Converti i video Instagram in trascrizioni rapide.",
      },
      tiktokToTranscript: {
        title: "TikTok in trascrizione",
        description: "Converti i video TikTok in trascrizioni rapide.",
      },
      instagramAnalytics: {
        title: "Analytics Reel Instagram",
        description: "Valuta hook, analizza commenti e ottieni brief creativi con IA.",
      },
      reelToPost: {
        title: "Reel per LinkedIn + Newsletter",
        description: "Trasforma i Reel in bozze per LinkedIn e email.",
      },
      itineraryExtractor: {
        title: "Estrattore di luoghi dai Reel",
        description: "Estrai tutti i luoghi citati in un Reel di viaggio.",
      },
      cooking: {
        title: "Video di cucina a ricetta",
        description: "Converti video di cucina in ricette.",
      },
      videoBlog: {
        title: "Video in blog",
        description: "Crea un articolo di blog a partire da un video.",
      },
      support: {
        title: "Supporto",
        description: "Contattaci, siamo qui per aiutare.",
      },
      feedback: {
        title: "Suggerimenti",
        description: "Lascia suggerimenti per migliorarci.",
      },
      blog: {
        title: "Blog",
        description: "Leggi i nostri articoli.",
      },
    },
    actions: {
      login: "Accedi",
      signup: "Registrati",
      dashboard: "La mia dashboard",
    },
  },
  pt: {
    links: {
      tools: "Ferramentas",
      useCases: "Casos de uso",
      pricing: "Preços",
      features: "Funcionalidades",
      resources: "Recursos",
      podcast: {
        title: "Transcrição de podcast",
        description: "Transcreva podcasts em texto limpo e pesquisavel.",
      },
      videoToText: {
        title: "Vídeo para texto",
        description: "Obtenha texto automaticamente a partir do vídeo.",
      },
      instagramToText: {
        title: "Instagram para transcrição",
        description: "Converta vídeos do Instagram em transcrições rápidas.",
      },
      tiktokToTranscript: {
        title: "TikTok para transcrição",
        description: "Converta vídeos do TikTok em transcrições rápidas.",
      },
      instagramAnalytics: {
        title: "Analytics de Reels do Instagram",
        description: "Pontue hooks, analise comentários e obtenha briefs criativos com IA.",
      },
      reelToPost: {
        title: "Reel para LinkedIn + Newsletter",
        description: "Converta Reels em rascunhos para LinkedIn e email.",
      },
      itineraryExtractor: {
        title: "Extrator de locais de viagem",
        description: "Extraia todos os lugares citados em um Reel de viagem.",
      },
      cooking: {
        title: "Vídeo de culinária para receita",
        description: "Transforme vídeos de culinária em receitas.",
      },
      videoBlog: {
        title: "Vídeo para blog",
        description: "Crie posts de blog a partir de vídeos.",
      },
      support: {
        title: "Suporte",
        description: "Fale conosco, teremos prazer em ajudar.",
      },
      feedback: {
        title: "Sugestoes",
        description: "Deixe sugestoes para melhorias.",
      },
      blog: {
        title: "Blog",
        description: "Leia nossos posts.",
      },
    },
    actions: {
      login: "Entrar",
      signup: "Cadastrar-se",
      dashboard: "Meu painel",
    },
  },
};

export function getNavigationCopy(locale: SupportedLocale): NavigationCopy {
  return navigationCopy[locale] ?? navigationCopy[DEFAULT_LOCALE];
}
